import React, { useEffect, useState } from 'react';
import { useCameraReady } from '../../../../utils/customHooks';
import { Headline } from '../../JoinStyle';
import { CameraPermissionContainer } from './CameraPermissionStyle';
import HoldAlertIcon from '../../../../atoms/icons/HoldAlertIcon';
import useMediaBreakpoints from '../../../../hooks/useMediaBreakpoints';
import DesktopInstruction from './DesktopInstruction';
import MobileInstruction from './MobileInstruction';
import LoadingBackdrop from '../../../../atoms/loader/LoadingBackdrop';
import QRCodeContent from './QRCode/QRCodeContent';
import QRCodeModal from './QRCode/QRCodeModal';
import { PUSHER } from '../../../../config/constants';
import { withPusher } from '../../../../contexts/PusherContext';
import { cameraPermissionModalStyle, selfieWallContentStyle } from '../SelfieVerificationStyle';
import BasicModal from '../../../../modules/modals/BasicModal';
import { refreshProfile, setFaceLivenessStatus } from 'actions/profileActions';
import { useDispatch } from 'react-redux';
import MasterConfig from 'config/Master';

interface CameraPermissionProps {
  setFaceLinenessStep: () => void;
  handleBackButtonClick: () => void;
  handleFaceLivelinessComplete: () => void;
  handleRedirectJoin: () => void;
  bindListener: ((listener: () => void) => void) | null;
  stopListening: ((listener: () => void) => void) | null;
  onRealtimeEvent: (event: string, callback: (data: any) => void) => void;
  isJoinFlow: boolean;
}

const CameraPermission: React.FC<CameraPermissionProps> = ({
  setFaceLinenessStep,
  handleBackButtonClick,
  handleFaceLivelinessComplete,
  handleRedirectJoin,
  bindListener,
  stopListening,
  onRealtimeEvent,
  isJoinFlow,
}) => {
  const { permissionStatus } = useCameraReady();
  const [showQRcode, setShowQRcode] = useState<boolean>(permissionStatus === 'NO_CAMERA_FOUND');
  const [showQRCodeModal, setShowQRCodeModal] = useState<boolean>(false);
  const { isMobile } = useMediaBreakpoints();
  const dispatch = useDispatch();

  useEffect(() => {
    bindListener && bindListener(listen);

    return () => {
      stopListening && stopListening(listen);
    };
  }, []);

  const listen = () => {
    const { EVENTS } = PUSHER;

    onRealtimeEvent(EVENTS.ACCOUNT_LIVENESS_ALERT, data => {
      if (MasterConfig.APP_ENV !== 'production') {
        console.log(`Notification Received from Pusher for event - ${EVENTS.ACCOUNT_LIVENESS_ALERT}`, data);
      }

      if (data?.data?.status === 'pending') {
        handleFaceLivelinessComplete();
        handleRedirectJoin();

        if (!isJoinFlow) {
          // Refresh profile to get the latest liveness status
          dispatch(refreshProfile());
          dispatch(setFaceLivenessStatus({ status: data?.data?.status, recommendation: data?.data?.recommendation }));
        }
      }
    });
  };

  useEffect(() => {
    if (permissionStatus === 'GRANTED') {
      setFaceLinenessStep();
    }

    if (permissionStatus === 'NO_CAMERA_FOUND') {
      setShowQRcode(true);
    }
  }, [permissionStatus]);

  const renderCameraPermisison = () => {
    return (
      <>
        <CameraPermissionContainer isJoinFlow={isJoinFlow}>
          <HoldAlertIcon />
          {showQRcode ? (
            <QRCodeContent isJoinFlow={isJoinFlow} />
          ) : (
            <>
              <Headline style={{ margin: '10px 0px' }}>Hold Up!</Headline>
              {isMobile ? (
                <MobileInstruction handleBackButtonClick={handleBackButtonClick} isJoinFlow={isJoinFlow} />
              ) : (
                <DesktopInstruction
                  setShowQRCodeModal={setShowQRCodeModal}
                  handleBackButtonClick={handleBackButtonClick}
                  isJoinFlow={isJoinFlow}
                />
              )}
            </>
          )}
        </CameraPermissionContainer>
        <QRCodeModal isOpen={showQRCodeModal} handleClose={() => setShowQRCodeModal(false)} isJoinFlow={isJoinFlow} />
      </>
    );
  };

  if (permissionStatus === 'NO_CAMERA_FOUND' || permissionStatus === 'PERMISSION_DENINED') {
    if (isJoinFlow) {
      return renderCameraPermisison();
    } else {
      return (
        <BasicModal
          isOpen={true}
          hideClose={true}
          customStyle={cameraPermissionModalStyle}
          customContentStyle={selfieWallContentStyle}
          shouldCloseOnOverlayClick={false}
          shouldCloseOnEsc={false}
          onClose={() => {
            //no action required
          }}
        >
          {renderCameraPermisison()}
        </BasicModal>
      );
    }
  }

  return (
    <>
      <LoadingBackdrop active={true} showLoadingBg={false} />{' '}
    </>
  );
};

export default withPusher(CameraPermission);
